/*
 * FALLBACK ONLY: reached solely from the legacy branch of firstPass, when no
 * aligner/WhisperX helper answers (phones). Kept working per project
 * constraint — do not extend.
 */

/**
 * Burst re-segmentation — "second-level refinement".
 *
 * WHY THIS EXISTS
 * ---------------
 * `refineBoundaries` fixes the model's time AXIS: it slides the whole timeline
 * onto the audio's own energy envelope, so a clip labelled word N stops playing
 * word N−7. What it cannot fix is how the labels are DISTRIBUTED once they are
 * in the right neighbourhood, because it only ever snaps to silence and inside a
 * continuous burst of speech there is no silence to snap to.
 *
 * And inside a burst is exactly where the first pass falls apart. Measured on a
 * 44.8 s Jordanian-Arabic recording:
 *
 *   "و"   (one phoneme)  was given 17.40–18.74  = 1.34 s
 *   "انه"                was given 28.38–31.46  = 3.08 s
 *   five consecutive words shared 26.70–27.02   = 60 ms each
 *
 * One label swallows the burst and its neighbours starve. Isolation QA then
 * fails almost everything in that burst (7/53 passed), and its repair tools do
 * not apply: padding a 60 ms sliver cannot take audio back from the swallower
 * (the midpoint clamp forbids it), and merging two starved words produces one
 * wrong segment instead of two.
 *
 * The fix is to stop repairing segments one at a time and re-cut the whole burst
 * at once. We already know WHAT was said there — the labels are the first pass's
 * strong suit. So we send the burst back alone with its label list and ask only
 * for times. See {@link REGION_RETIME_PROMPT} in `gemini.ts`.
 *
 * HOW
 * ---
 * 1. BURSTS.  Same VAD as `refineBoundaries` (its envelope is imported, not
 *    re-implemented); runs of speech separated by < 120 ms are one burst.
 * 2. REGIONS. Contiguous runs of QA failures, plus any segment whose duration is
 *    pathological on its face (a 60 ms word, or a 3 s "و"), SPLIT BY BURST and
 *    grown out to the edges of the burst they sit in, with up to 0.3 s of
 *    context — but never across a neighbour that PASSED QA. A passing segment is
 *    proven-good audio; nothing here is allowed to touch it.
 * 3. RE-TIME. One Gemini call per region. The exception is a single item alone
 *    in a burst short enough to BE that item: there the burst edges are the
 *    answer and no call is made.
 * 4. REBUILD. Relative times are mapped back, forced monotonic, non-overlapping,
 *    inside the region and at least 60 ms long, and then RE-ANCHORED onto the
 *    spectral onsets measured in the region (see `anchorPlacedToOnsets`), which
 *    replaced a linear renormalisation that only fixed the answer's overall
 *    span. If anchoring declines, the boundaries are snapped to any speech edge
 *    within range and the region's rim is covered instead. The region's segments
 *    are rewritten atomically, their clips re-cut, and each rebuilt clip goes
 *    back through isolation QA once.
 *
 * BEST EFFORT, ALWAYS
 * -------------------
 * Every region is independent and every failure mode — a dead API, an answer
 * that violates the ordering rules, a region that would not fit its items — ends
 * in "leave those segments exactly as they were". This stage can only ever
 * improve a task or do nothing to it.
 */

import { newId, reviseSegments, type SegmentRevision } from '../../db';
import { round3 } from '../../lib/num';
import type { Segment } from '../../types';
import { extractMono, sliceToWav } from '../audioSlicer';
import type { ClipQaAnswer, RegionRetimeItem } from '../gemini';
import {
  computeSpeechEnvelope,
  findSpeechBursts,
  snapToEdge,
  type Burst,
  type SpeechEnvelope,
} from '../vad';
import {
  letterCount,
  expectedDurationRange,
  placeSegmentsInBurst,
  scoreBurst,
} from './onsetAlign';
import { detectOnsets, type OnsetCandidate } from '../onsetDetect';

/*
 * Both live in `refineBoundaries` now, next to the envelope they read, so every
 * stage that says "burst" means the same frames. Re-exported here because this
 * is where callers have always imported them from.
 */
/* The `findSpeechBursts`/`Burst` re-export that used to sit here was pure
 * laundering: consumers now import them from `services/vad` directly. */

/* ------------------------------------------------------------------ */
/* Tunables                                                            */
/* ------------------------------------------------------------------ */

/** Context added on each side of a region, when a neighbour allows it. */
const CONTEXT_SEC = 0.3;

/** Nothing this stage produces is ever shorter than this. */
const MIN_ITEM_SEC = 0.06;

/** Below this a segment is pathological on its face, whatever QA said. */
const IMPLAUSIBLE_SHORT_SEC = 0.12;

/** Above this a `word` with a short label is pathological, whatever QA said. */
const IMPLAUSIBLE_LONG_SEC = 1.0;

/** "Short label" for the rule above, counted in letters. */
const SHORT_LABEL_LETTERS = 6;

/**
 * Is this label ONE word?
 *
 * The "a short word cannot run for a second" rules below are statements about a
 * single lexical item. Since burst-first grouping a label can legitimately be
 * several words joined with spaces ("انا اسمي امجد"), and a multi-word label is
 * SUPPOSED to be long — judging one by the single-word ceiling would mark every
 * fused group pathological and hand the whole recording back to a re-segmentation
 * stage that grouping exists to make unnecessary. Letter count alone cannot tell
 * the two apart: "مو فاهم" is 6 letters and two words.
 */
function isSingleWord(text: string): boolean {
  return (text ?? '').trim().split(/\s+/).filter(Boolean).length <= 1;
}

/**
 * Hard ceiling on a rebuilt short-label `word`. The prompt says a word is rarely
 * over 1.2 s; when the model ignores that and hands one item most of the region
 * anyway, we trim the tail rather than accept a fresh swallow. Trimming is safe
 * because segments need not be contiguous — the surplus simply becomes a gap.
 */
const MAX_SHORT_WORD_SEC = 1.2;

/** Snap radius onto a speech edge for a rebuilt boundary. */
const SNAP_SEC = 0.1;

/** Speech left uncovered at a region's rim before {@link coverSpeechEdges} acts. */
const EDGE_SLACK_SEC = 0.15;

/** Regions longer than this are not one burst, they are a sentence. Skip. */
const MAX_REGION_SEC = 6;

/** More items than this in one region means the grouping went wrong. Skip. */
const MAX_REGION_ITEMS = 24;

/** Budget: at most this many regions are sent to Gemini per task. */
const MAX_REGION_CALLS = 30;

/** Region clips are sent at 16 kHz mono, like every other QA request. */
const REGION_SAMPLE_RATE = 16000;

/* ------------------------------------------------------------------ */
/* Shapes shared with the QA loop                                      */
/* ------------------------------------------------------------------ */

/**
 * The mutable per-segment record the clip-QA loop maintains.
 *
 * Re-segmentation edits this list IN PLACE — splicing rebuilt segments over the
 * originals — so that the stages after it (padded retries, merges, flagging)
 * operate on the new segmentation without knowing this stage exists.
 */
export interface ResegmentItem {
  seg: Segment;
  /** `undefined` while unknown (request failed) — never treated as a failure. */
  passed?: boolean;
  /** The segment row changed and must be written back. */
  dirty: boolean;
  /** The boundaries moved, so the stored clip must be re-cut. */
  clipDirty: boolean;
  /** Absorbed by a merge — delete the row and its clip. */
  removed: boolean;
}

export interface ResegmentStats {
  /** Regions identified as needing a rebuild. */
  regionsFound: number;
  /** Regions actually sent to Gemini (budget and size caps applied). */
  regionsAttempted: number;
  /** Regions whose answer was accepted and written. */
  regionsRebuilt: number;
  /** Single-item regions solved from the VAD alone, with no API call. */
  regionsSnapped: number;
  /** Regions whose answer was re-anchored onto the onsets measured in it. */
  regionsOnsetAnchored: number;
  /** Existing segments given new boundaries. */
  segmentsRewritten: number;
  /** Segments the model heard that the first pass had not listed. */
  segmentsInserted: number;
  /** Listed segments the model did not hear at all; deleted. */
  segmentsDropped: number;
  /** Of those, segments deleted as a mis-timed duplicate of a nearby run. */
  duplicateCramsDropped: number;
  /** Rebuilt segments that then passed isolation QA. */
  passedAfterRebuild: number;
  /** Rebuilt short-label words trimmed back to {@link MAX_SHORT_WORD_SEC}. */
  clampedLongWords: number;
  /** Region re-timing requests issued (retries excluded — the caller counts those). */
  apiCalls: number;
  /** Regions left untouched because the QA time budget ran out. */
  regionsSkippedByBudget: number;
}

export interface ResegmentDeps {
  buffer: AudioBuffer;
  /** Padding the stored clips are cut with, so re-QA hears the same audio. */
  padSec?: number;
  signal?: AbortSignal;
  /**
   * `Date.now()` past which no further region may be sent.
   *
   * This stage is the most expensive thing in the app per unit of benefit: one
   * re-timing call plus one isolation call per rebuilt segment, all sequential.
   * Against an API whose latency has been measured anywhere from 0.5 s to 15
   * minutes for the same request, that is unbounded wall-clock time, and it is
   * how a run once spent two hours in QA. Regions already processed are
   * persisted, so stopping at the deadline is always safe.
   */
  deadlineAt?: number;
  /** Send one region for re-timing. Null on any failure the caller gave up on. */
  askRegion: (
    startSec: number,
    endSec: number,
    labels: string[],
    clip: Blob,
    /** Where the VAD measured speech, relative to the clip. */
    speech: { startSec: number; endSec: number },
  ) => Promise<RegionRetimeItem[] | null>;
  /** Re-listen to one window in isolation. Null = unknown, never a failure. */
  askClip: (startSec: number, endSec: number) => Promise<ClipQaAnswer | null>;
  /** The QA loop's own pass/fail judgement, so both stages agree. */
  passes: (seg: Pick<Segment, 'heard' | 'kind'>, answer: ClipQaAnswer) => boolean;
  /** Called once per region processed. */
  onProgress?: (done: number, total: number) => void;
}

/* ------------------------------------------------------------------ */
/* 1 — which segments need a rebuild                                   */
/* ------------------------------------------------------------------ */

/**
 * Is this segment's duration impossible for what it claims to be?
 *
 * Checked independently of QA, because the two failure modes are complementary:
 * a 60 ms sliver often still "passes" (the model obligingly hears the expected
 * word in a fragment) and a swallower usually passes too — it contains its own
 * word plus five others. Duration is the evidence QA cannot see.
 */
export function isPathological(seg: Pick<Segment, 'startSec' | 'endSec' | 'heard' | 'kind'>): boolean {
  const dur = seg.endSec - seg.startSec;
  if (dur < IMPLAUSIBLE_SHORT_SEC) return true;
  // Fillers are exempt from the long rule on purpose: "ummmmm" really can run
  // for two seconds, and cutting it short is the one thing worse than leaving it.
  if (
    seg.kind === 'word' &&
    dur > IMPLAUSIBLE_LONG_SEC &&
    isSingleWord(seg.heard) &&
    letterCount(seg.heard) <= SHORT_LABEL_LETTERS
  ) {
    return true;
  }
  return false;
}

/**
 * Would this window make a short word implausibly long?
 *
 * Shared with the QA loop's padded retries: widening a clip until Gemini
 * recognises it is a good way to fix a boundary that was cut too tight, and a
 * bad way to fix anything else — past a point the clip is simply large enough to
 * contain the word AND its neighbours, and "recognised" stops being evidence
 * about the boundary. Fillers are exempt: an elongated "ummmm" really is long.
 */
export function isImplausiblyLong(
  seg: Pick<Segment, 'heard' | 'kind'>,
  durationSec: number,
): boolean {
  if (seg.kind !== 'word') return false;
  if (!isSingleWord(seg.heard)) return false;
  return durationSec > MAX_SHORT_WORD_SEC && letterCount(seg.heard) <= SHORT_LABEL_LETTERS;
}

function needsRebuild(item: ResegmentItem): boolean {
  return item.passed === false || isPathological(item.seg);
}

interface Region {
  /** Inclusive index range into the item list. */
  from: number;
  to: number;
  /** The burst this region re-cuts. */
  burst: Burst;
  /** Audio window to cut and re-time. */
  startSec: number;
  endSec: number;
}

/** Distance at which a segment sitting in silence still belongs to a burst. */
const ATTACH_SEC = 0.15;

/**
 * Which burst does this segment live in? −1 when it sits in silence.
 *
 * Chosen by overlap, because a mis-timed segment routinely straddles the edge of
 * the burst it belongs to. A segment with no overlap at all is usually a sliver
 * the first pass parked in a gap; it is attached to a burst within
 * {@link ATTACH_SEC}, and otherwise left alone — there is no audio to give it.
 */
function burstOf(seg: Pick<Segment, 'startSec' | 'endSec'>, bursts: Burst[]): number {
  let best = -1;
  let bestOverlap = 0;
  for (let i = 0; i < bursts.length; i++) {
    const overlap = Math.min(seg.endSec, bursts[i].endSec) - Math.max(seg.startSec, bursts[i].startSec);
    if (overlap > bestOverlap) {
      bestOverlap = overlap;
      best = i;
    }
  }
  if (best >= 0) return best;

  let nearest = -1;
  let nearestDist = ATTACH_SEC;
  for (let i = 0; i < bursts.length; i++) {
    const dist =
      seg.startSec >= bursts[i].endSec ? seg.startSec - bursts[i].endSec : bursts[i].startSec - seg.endSec;
    if (dist >= 0 && dist < nearestDist) {
      nearestDist = dist;
      nearest = i;
    }
  }
  return nearest;
}

/**
 * Turn suspect segments into audio regions — ONE BURST AT A TIME.
 *
 * Two rules do the work, and the second one is not optional. A region is a
 * contiguous run of suspect segments, so that a neighbour which passed QA always
 * ends the run: its audio is proven good and is not ours to redistribute. But a
 * run is then SPLIT by burst, because on a recording where most clips fail QA
 * the runs are enormous — a first attempt without this produced two regions
 * covering the entire 45 s file, both instantly rejected for being longer than a
 * burst can possibly be, and the stage did nothing at all.
 *
 * Splitting by burst is also the honest model of the bug: labels are
 * mis-distributed WITHIN a continuous stretch of speech, and audio never has to
 * move across a silence to fix that.
 */
export function planRegions(
  items: ResegmentItem[],
  bursts: Burst[],
  durationSec: number,
): Region[] {
  const regions: Region[] = [];
  const home = items.map((it) => burstOf(it.seg, bursts));
  let i = 0;

  while (i < items.length) {
    if (!needsRebuild(items[i]) || home[i] < 0) {
      i++;
      continue;
    }
    // one run, one burst
    let end = i;
    while (end + 1 < items.length && needsRebuild(items[end + 1]) && home[end + 1] === home[i]) end++;

    const burst = bursts[home[i]];
    const groupStart = items[i].seg.startSec;
    const groupEnd = items[end].seg.endSec;

    let from = Math.min(groupStart, burst.startSec) - CONTEXT_SEC;
    let to = Math.max(groupEnd, burst.endSec) + CONTEXT_SEC;

    const prevEnd = i > 0 ? items[i - 1].seg.endSec : 0;
    const nextStart = end + 1 < items.length ? items[end + 1].seg.startSec : durationSec;
    from = Math.max(0, prevEnd, from);
    to = Math.min(durationSec, nextStart, to);

    if (to - from > MIN_ITEM_SEC) {
      regions.push({ from: i, to: end, burst, startSec: from, endSec: to });
    }
    i = end + 1;
  }

  return regions;
}

/* ------------------------------------------------------------------ */
/* 3 — turn one answer into segments                                   */
/* ------------------------------------------------------------------ */

interface Placed {
  /** The original item this replaces, or null for something newly heard. */
  source: ResegmentItem | null;
  startSec: number;
  endSec: number;
  text: string;
  kind: Segment['kind'];
}

/**
 * Map the model's clip-relative answer onto the recording's timeline and force
 * it to obey every invariant the rest of the app relies on.
 *
 * Returns null when the answer cannot be made to fit — a duplicated or
 * out-of-order `sourceIndex`, an empty list, or more items than the region has
 * room for at 60 ms each. Rejecting is always safe: the caller keeps what it had.
 */
export function placeRegionItems(
  answer: RegionRetimeItem[],
  originals: ResegmentItem[],
  regionStart: number,
  regionEnd: number,
): Placed[] | null {
  if (answer.length === 0) return null;
  // The model may legitimately hear MORE than it was told about — that is how a
  // swallowed word gets its audio back — but not without limit.
  if (answer.length > MAX_REGION_ITEMS || answer.length > originals.length * 3 + 4) return null;
  // The whole bug being fixed is items crammed below any plausible word length.
  // An answer that cannot give every item 0.12 s is the bug again, not a repair.
  if (regionEnd - regionStart < answer.length * IMPLAUSIBLE_SHORT_SEC) return null;

  // Sort by the times the model gave, so a list returned slightly out of order
  // is repaired rather than rejected; `sourceIndex` monotonicity is checked
  // afterwards, against that time order.
  const rows = [...answer].sort((a, b) => a.startSec - b.startSec || a.endSec - b.endSec);

  const used = new Set<number>();
  let lastSource = -1;
  const placed: Placed[] = [];

  for (const row of rows) {
    let source: ResegmentItem | null = null;
    if (row.sourceIndex >= 0 && row.sourceIndex < originals.length) {
      // A repeat or a step backwards means the model lost track of the list; its
      // times can no longer be trusted to belong to the labels it names.
      if (used.has(row.sourceIndex) || row.sourceIndex < lastSource) return null;
      used.add(row.sourceIndex);
      lastSource = row.sourceIndex;
      source = originals[row.sourceIndex];
    } else if (!row.text.trim()) {
      // Something unlisted AND unnamed is not a segment we can label.
      continue;
    }
    placed.push({
      source,
      startSec: regionStart + row.startSec,
      endSec: regionStart + row.endSec,
      text: row.text.trim(),
      kind: row.kind,
    });
  }
  if (placed.length === 0) return null;

  // Order, non-overlap, minimum length, inside the region.
  let cursor = regionStart;
  for (let i = 0; i < placed.length; i++) {
    const p = placed[i];
    const remaining = placed.length - i - 1;
    const latest = regionEnd - remaining * MIN_ITEM_SEC - MIN_ITEM_SEC;
    p.startSec = Math.min(Math.max(p.startSec, cursor), latest);
    p.endSec = Math.max(p.endSec, p.startSec + MIN_ITEM_SEC);
    if (p.endSec > regionEnd - remaining * MIN_ITEM_SEC) {
      p.endSec = regionEnd - remaining * MIN_ITEM_SEC;
    }
    if (p.endSec - p.startSec < MIN_ITEM_SEC) return null;
    cursor = p.endSec;
  }

  return placed;
}

/**
 * Re-anchor a region's answer onto the acoustic onsets inside it.
 *
 * REPLACES a linear renormalisation that used to live here. That version fixed
 * a real and reproducible bias — told a clip holds five items, the model bunches
 * all five into the front and leaves the back of the burst unaccounted for — by
 * stretching the answer's span onto the span the VAD measured. It worked on the
 * total, and that was its whole problem: stretching preserves the model's
 * PROPORTIONS, so five items invented at 20% of the burst each still land at 20%
 * of the burst each, just further apart. The boundaries were still guesses; they
 * were merely well-distributed guesses.
 *
 * Onsets are measurable. `placeSegmentsInBurst` lays the items onto the spectral
 * onsets actually present in the region, in order, trading movement away from
 * the model's answer against duration plausibility and onset strength — so a
 * bunched answer is spread out AND each boundary lands where a syllable starts.
 *
 * Returns false (leaving `placed` untouched) when there is no feasible layout or
 * when the result would score worse than the model's own answer.
 */
function anchorPlacedToOnsets(
  placed: Placed[],
  env: SpeechEnvelope,
  onsets: readonly OnsetCandidate[],
  speechStart: number,
  speechEnd: number,
): boolean {
  if (placed.length < 2) return false;
  if (speechEnd - speechStart <= 0) return false;

  const segs = placed.map((p) => ({
    startSec: p.startSec,
    endSec: p.endSec,
    heard: p.source ? p.source.seg.heard : p.text,
    kind: p.source ? p.source.seg.kind : p.kind,
  }));
  const candidates = onsets.filter((o) => o.timeSec >= speechStart && o.timeSec < speechEnd);

  const placement = placeSegmentsInBurst(segs, speechStart, speechEnd, candidates);
  if (!placement || placement.crammed) return false;

  const rebuilt = segs.map((s, k) => ({
    ...s,
    startSec: placement.starts[k],
    endSec: placement.ends[k],
  }));
  if (
    scoreBurst(rebuilt, env, speechStart, speechEnd) <
    scoreBurst(segs, env, speechStart, speechEnd) - 1e-9
  ) {
    return false;
  }

  for (let i = 0; i < placed.length; i++) {
    placed[i].startSec = rebuilt[i].startSec;
    placed[i].endSec = rebuilt[i].endSec;
  }
  return true;
}

/**
 * Give the head and tail of the measured speech back to the items at each end.
 *
 * The model's answer is routinely late or short at a region's rim: it returned
 * "حلوه" at 25.19–25.69 for a region whose speech starts at 24.18, leaving a full
 * second of audible speech with no label over it. Something IS being said there
 * and the item at that end is the only label available for it, so the boundary
 * is extended — but only while the item stays inside its own plausible duration,
 * because the alternative failure (one label swallowing the whole burst) is the
 * bug this module exists to undo.
 */
function coverSpeechEdges(placed: Placed[], speechStart: number, speechEnd: number): void {
  const fits = (p: Placed, startSec: number, endSec: number): boolean => {
    const label = p.source ? p.source.seg.heard : p.text;
    const kind = p.source ? p.source.seg.kind : p.kind;
    return endSec - startSec <= expectedDurationRange(label, kind).maxSec;
  };

  const first = placed[0];
  if (first.startSec - speechStart > EDGE_SLACK_SEC && fits(first, speechStart, first.endSec)) {
    first.startSec = speechStart;
  }
  const last = placed[placed.length - 1];
  if (speechEnd - last.endSec > EDGE_SLACK_SEC && fits(last, last.startSec, speechEnd)) {
    last.endSec = speechEnd;
  }
}

/**
 * Nudge rebuilt boundaries onto real energy edges.
 *
 * Inside a burst there are no edges, so most boundaries are left exactly where
 * the model put them — which is correct: the model is the only source of
 * information there. The gain is at the region's rim and around the short
 * internal dips the VAD did resolve. The whole snap is discarded if it breaks
 * the ordering it was applied to.
 */
function snapPlaced(placed: Placed[], env: SpeechEnvelope, regionStart: number, regionEnd: number): void {
  const snapped = placed.map((p) => ({
    start: snapToEdge(p.startSec, env, SNAP_SEC, true),
    end: snapToEdge(p.endSec, env, SNAP_SEC, false),
  }));

  let cursor = regionStart;
  for (let i = 0; i < snapped.length; i++) {
    const s = snapped[i];
    if (s.start < cursor || s.end <= s.start + MIN_ITEM_SEC - 1e-9) return;
    if (s.end > regionEnd + 1e-9) return;
    cursor = s.end;
  }
  for (let i = 0; i < placed.length; i++) {
    placed[i].startSec = snapped[i].start;
    placed[i].endSec = snapped[i].end;
  }
}

/* ------------------------------------------------------------------ */
/* Duplicated crams                                                    */
/* ------------------------------------------------------------------ */

/** How far away the properly-timed copy of a cram may sit. */
const DUPLICATE_WINDOW_SEC = 5;

/** Labels compared for the duplicate test: letters and digits only. */
function fold(text: string): string {
  return (text.toLowerCase().match(/[\p{L}\p{N}]/gu) ?? []).join('');
}

/**
 * Find crams whose words turned out to live somewhere else.
 *
 * The case is real and this recording has it. The first pass assigned five words
 * to a 0.32 s burst — 60 ms each — when the burst holds one short word at most.
 * Re-timing that burst cannot help: asked to place five items in 0.32 s of
 * speech, the model dutifully places five items in 0.32 s of speech. What DOES
 * happen is that the neighbouring burst, re-timed on its own, comes back with
 * those same five words at proper lengths, because that is where they were
 * actually spoken. The recording now contains each of them twice.
 *
 * So: a run where EVERY item is below any plausible word length, whose exact
 * label sequence also appears nearby occupying at least twice as much audio, is
 * the mis-timed copy, and is deleted. Both halves of that test are load-bearing.
 * A speaker who genuinely repeats themselves produces two copies of comparable
 * length and neither is deleted; and two consecutive real words cannot fit in
 * the 0.24 s that the cram side of the test demands.
 *
 * The comparison is by SPAN rather than per item, because the properly-timed
 * copy is itself a re-timed guess and routinely has one member a shade under
 * 0.12 s. What separates the two is the whole run: 0.32 s against 1.37 s.
 */
export function findDuplicatedCrams(items: ResegmentItem[]): ResegmentItem[] {
  const doomed: ResegmentItem[] = [];
  const short = items.map((it) => it.seg.endSec - it.seg.startSec < IMPLAUSIBLE_SHORT_SEC);
  const labels = items.map((it) => fold(it.seg.heard));
  const span = (from: number, to: number) => items[to].seg.endSec - items[from].seg.startSec;

  let i = 0;
  while (i < items.length) {
    if (!short[i]) {
      i++;
      continue;
    }
    let end = i;
    while (end + 1 < items.length && short[end + 1]) end++;
    const run = end - i + 1;

    if (run >= 2) {
      const wanted = labels.slice(i, end + 1);
      const cramSpan = span(i, end);
      for (let j = 0; j + run <= items.length; j++) {
        if (j + run > i && j <= end) continue; // the run itself, or overlapping it
        let same = true;
        for (let k = 0; k < run; k++) {
          if (!wanted[k] || labels[j + k] !== wanted[k]) {
            same = false;
            break;
          }
        }
        if (!same) continue;
        if (Math.abs(items[j].seg.startSec - items[i].seg.startSec) > DUPLICATE_WINDOW_SEC) continue;
        const copySpan = span(j, j + run - 1);
        if (copySpan < cramSpan * 2) continue;
        if (copySpan / run < IMPLAUSIBLE_SHORT_SEC) continue;
        for (let k = i; k <= end; k++) doomed.push(items[k]);
        break;
      }
    }
    i = end + 1;
  }
  return doomed;
}

/* ------------------------------------------------------------------ */
/* Public entry point                                                  */
/* ------------------------------------------------------------------ */

/**
 * Re-cut every burst the QA loop could not repair, in place.
 *
 * `items` is mutated: rebuilt regions are spliced over their originals, so the
 * caller's later stages see one flat, correctly ordered list. Each region is
 * persisted as it completes (segments, clips and the renumbering it forces, in
 * one transaction) so an abort halfway through still leaves a coherent task.
 */
export async function resegmentBursts(
  items: ResegmentItem[],
  deps: ResegmentDeps,
): Promise<ResegmentStats> {
  const { buffer, padSec, signal, deadlineAt, askRegion, askClip, passes, onProgress } = deps;
  const outOfTime = (): boolean => deadlineAt !== undefined && Date.now() > deadlineAt;

  const stats: ResegmentStats = {
    regionsFound: 0,
    regionsAttempted: 0,
    regionsRebuilt: 0,
    regionsSnapped: 0,
    regionsOnsetAnchored: 0,
    segmentsRewritten: 0,
    segmentsInserted: 0,
    segmentsDropped: 0,
    duplicateCramsDropped: 0,
    passedAfterRebuild: 0,
    clampedLongWords: 0,
    apiCalls: 0,
    regionsSkippedByBudget: 0,
  };
  if (items.length === 0) return stats;

  const duration = buffer.duration;
  const mono = extractMono(buffer, 0, duration);
  const env = computeSpeechEnvelope(mono, buffer.sampleRate);
  const bursts = findSpeechBursts(env);
  if (bursts.length === 0) return stats;
  // One flux pass for the whole file; each region reads the slice it needs.
  const { onsets } = detectOnsets(mono, buffer.sampleRate);

  const regions = planRegions(items, bursts, duration);
  stats.regionsFound = regions.length;
  if (import.meta.env.DEV) {
    // The region plan is the one thing you cannot reconstruct from the finished
    // task, and it is what you need when a burst comes back wrong.
    console.info(
      '[lableforge] resegment plan',
      regions.map((r) => `${r.startSec.toFixed(2)}-${r.endSec.toFixed(2)} [${r.from}..${r.to}]`),
    );
  }
  if (regions.length === 0) return stats;

  const taskId = items[0].seg.taskId;
  let processed = 0;
  let calls = 0;

  /**
   * Write the current item list back, contiguously numbered, in one transaction.
   *
   * Called once per region rather than once at the end so an abort halfway
   * through leaves a coherent task rather than a half-renumbered one. Anything
   * written is marked clean; the QA stages after this one decide what is dirty
   * next.
   */
  async function persist(deleteIds: string[]): Promise<void> {
    const revisions: SegmentRevision[] = [];
    items.forEach((item, index) => {
      if (item.seg.index !== index) {
        item.seg.index = index;
        item.dirty = true;
      }
      if (!item.dirty) return;
      revisions.push(
        item.clipDirty
          ? {
              segment: item.seg,
              clip: sliceToWav(buffer, item.seg.startSec, item.seg.endSec, { padSec }),
            }
          : { segment: item.seg },
      );
      item.dirty = false;
      item.clipDirty = false;
    });
    await reviseSegments(revisions, deleteIds);
  }

  /*
   * Right to left. A rebuild changes how many items sit in the list, which would
   * invalidate every index planned after it; walking backwards means the indices
   * still ahead of us are the ones we have not touched.
   */
  for (let r = regions.length - 1; r >= 0; r--) {
    if (signal?.aborted) break;
    const region = regions[r];
    const originals = items.slice(region.from, region.to + 1);
    const report = () => onProgress?.(++processed, regions.length);

    /*
     * Re-clamp against the CURRENT neighbours rather than the planned ones. Two
     * regions separated by a silence both plan to reach into that silence for
     * context, so their planned windows overlap — and a region rebuilt a moment
     * ago may have pulled its first item left into exactly that gap. Walking
     * right to left means the neighbour on our right is already final, so asking
     * it again here is what keeps the rebuilt segments from colliding.
     */
    const prevEnd = region.from > 0 ? items[region.from - 1].seg.endSec : 0;
    const nextStart = region.to + 1 < items.length ? items[region.to + 1].seg.startSec : duration;
    const regionStart = Math.max(0, prevEnd, region.startSec);
    const regionEnd = Math.min(duration, nextStart, region.endSec);

    if (
      regionEnd - regionStart <= MIN_ITEM_SEC ||
      regionEnd - regionStart > MAX_REGION_SEC ||
      originals.length > MAX_REGION_ITEMS
    ) {
      report();
      continue;
    }

    // The audible part of the region, measured. Both the prompt and the
    // renormalisation below are anchored to it.
    const speechStart = Math.max(regionStart, region.burst.startSec);
    const speechEnd = Math.min(regionEnd, region.burst.endSec);

    let placed: Placed[] | null = null;

    /*
     * One item alone in a burst SHORT enough to be that one item needs no model:
     * the region is the item, and the burst edges are measured rather than
     * guessed. The length test is what makes this safe — a lone segment holding
     * a 2.6 s burst is the swallower case, the burst demonstrably contains more
     * than we were told, and only the model can say what.
     */
    if (originals.length === 1) {
      const start = speechStart;
      const end = speechEnd;
      const dur = end - start;
      const unchanged =
        Math.abs(start - originals[0].seg.startSec) < 1e-3 &&
        Math.abs(end - originals[0].seg.endSec) < 1e-3;
      if (dur >= IMPLAUSIBLE_SHORT_SEC && dur <= MAX_SHORT_WORD_SEC) {
        if (unchanged) {
          report();
          continue;
        }
        placed = [
          {
            source: originals[0],
            startSec: start,
            endSec: end,
            text: originals[0].seg.heard,
            kind: originals[0].seg.kind,
          },
        ];
        stats.regionsSnapped++;
      }
    }

    if (!placed) {
      if (calls >= MAX_REGION_CALLS) {
        report();
        continue;
      }
      if (outOfTime()) {
        stats.regionsSkippedByBudget++;
        report();
        continue;
      }
      calls++;
      stats.regionsAttempted++;
      const clip = sliceToWav(buffer, regionStart, regionEnd, {
        padSec: 0,
        minSec: 0,
        targetSampleRate: REGION_SAMPLE_RATE,
      });
      const answer = await askRegion(
        regionStart,
        regionEnd,
        originals.map((o) => o.seg.heard),
        clip,
        { startSec: speechStart - regionStart, endSec: speechEnd - regionStart },
      );
      stats.apiCalls++;
      if (!answer) {
        report();
        continue;
      }
      placed = placeRegionItems(answer, originals, regionStart, regionEnd);
      if (!placed) {
        report();
        continue;
      }
      // Onset anchoring already puts every boundary on measured evidence, so the
      // silence snap only runs when it declined — snapping an anchored boundary
      // could only drag it back off the onset it was just placed on.
      if (anchorPlacedToOnsets(placed, env, onsets, speechStart, speechEnd)) {
        stats.regionsOnsetAnchored++;
      } else {
        snapPlaced(placed, env, regionStart, regionEnd);
        // Onset anchoring already pins the first item to the speech start and
        // the last to the speech end; this is the fallback path's version.
        coverSpeechEdges(placed, speechStart, speechEnd);
      }
      stats.regionsRebuilt++;
    }

    /* -- build the new segment rows --------------------------------- */
    const rebuilt: ResegmentItem[] = [];
    for (const p of placed) {
      let start = round3(p.startSec);
      let end = round3(p.endSec);
      const kind = p.source ? p.source.seg.kind : p.kind;
      const label = p.source ? p.source.seg.heard : p.text;
      if (
        kind === 'word' &&
        isSingleWord(label) &&
        letterCount(label) <= SHORT_LABEL_LETTERS &&
        end - start > MAX_SHORT_WORD_SEC
      ) {
        end = round3(start + MAX_SHORT_WORD_SEC);
        stats.clampedLongWords++;
      }
      if (end <= start) end = round3(start + MIN_ITEM_SEC);
      start = Math.max(0, start);
      end = Math.min(duration, end);

      if (p.source) {
        rebuilt.push({
          seg: { ...p.source.seg, startSec: start, endSec: end, verify: 'pending' },
          dirty: true,
          clipDirty: true,
          removed: false,
        });
        stats.segmentsRewritten++;
      } else {
        rebuilt.push({
          seg: {
            id: newId(),
            taskId,
            index: 0, // renumbered below
            startSec: start,
            endSec: end,
            heard: p.text,
            contextual: p.text,
            // Middling, not flagged: this label has never been checked, and the
            // isolation pass immediately below is what will decide.
            confidence: 0.5,
            kind: p.kind,
            verify: 'pending',
          },
          dirty: true,
          clipDirty: true,
          removed: false,
        });
        stats.segmentsInserted++;
      }
    }

    const kept = new Set(rebuilt.map((it) => it.seg.id));
    const dropped = originals.filter((o) => !kept.has(o.seg.id));
    stats.segmentsDropped += dropped.length;

    /* -- splice, renumber, persist ---------------------------------- */
    items.splice(region.from, region.to - region.from + 1, ...rebuilt);
    await persist(dropped.map((o) => o.seg.id));

    /* -- one round of isolation QA on what we just built -------------- */
    for (const item of rebuilt) {
      if (signal?.aborted || outOfTime()) break;
      const check = await askClip(item.seg.startSec, item.seg.endSec);
      if (!check) continue;
      item.passed = passes(item.seg, check);
      if (item.passed) stats.passedAfterRebuild++;
    }

    report();
  }

  /* -- drop crams whose words were found elsewhere -------------------- */
  if (!signal?.aborted) {
    const doomed = findDuplicatedCrams(items);
    if (doomed.length > 0) {
      const ids = new Set(doomed.map((it) => it.seg.id));
      for (let i = items.length - 1; i >= 0; i--) {
        if (ids.has(items[i].seg.id)) items.splice(i, 1);
      }
      stats.segmentsDropped += doomed.length;
      stats.duplicateCramsDropped = doomed.length;
      await persist([...ids]);
    }
  }

  return stats;
}

export default resegmentBursts;
